// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import { ResizeMode, Video, type AVPlaybackStatus } from 'expo-av';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ActivityIndicator, Image, Platform, Pressable, StyleSheet, Text, View } from 'react-native';

import { HoleMapCanvas, type MapOverlayPolygon } from '@/features/hole/holeview/HoleMapCanvas';
import { OVERLAY_COLORS } from '@/features/hole/holeview/holeShapes';
import { buildTileUrl, computeLayoutAt } from '@/features/hole/holeview/tileMath';
import { getSatelliteTileUrlTemplate } from '@/features/hole/holeview/tileSource';
import { formatHoleDistance } from '@/features/units/distance';
import { useI18n } from '@/i18n';
import { useDistanceUnit } from '@/stores/distanceUnitState';
import appColors from '@/theme/colors';

import { buildHoleCameraPath } from './flyoverCamera';
import type { FlyoverHole } from './useCourseFlyover';

export type FlyoverPlayerProps = {
  courseName: string;
  /** Holes worth flying, course order — `useCourseFlyover`'s output. */
  holes: FlyoverHole[];
  loading: boolean;
  unavailable: boolean;
  onClose: () => void;
  /**
   * Server-rendered flyover MP4s per hole, where the offline render script has produced
   * them. A hole with a video plays it; every other hole plays the live 2D flyover.
   */
  videoUrlByHole?: Record<number, string>;
};

/** Roughly the size the canvas renders at; only used to list tiles worth prefetching. */
const PREFETCH_VIEWPORT = { width: 400, height: 400 };

/**
 * Plays a course hole by hole: the camera lifts off the tee, glides down the line of play
 * and settles on the green, then moves to the next hole.
 *
 * The animation drives `HoleMapCanvas` through its `camera` prop — one
 * `requestAnimationFrame` loop advancing a clock, the camera path turning that clock into
 * a centre. The span and bearing are constant per hole, so the tile zoom level never
 * changes mid-flight and panning reuses the mounted tiles.
 */
export function FlyoverPlayer({
  courseName,
  holes,
  loading,
  unavailable,
  onClose,
  videoUrlByHole,
}: FlyoverPlayerProps) {
  const { t } = useI18n();
  const distanceUnit = useDistanceUnit();
  const [holeIndex, setHoleIndex] = useState(0);
  const [playing, setPlaying] = useState(true);
  const [clockMs, setClockMs] = useState(0);

  const hole = holes[holeIndex] ?? null;
  const videoUrl = hole ? (videoUrlByHole?.[hole.holeNumber] ?? null) : null;
  const path = useMemo(
    () => (hole && hole.green ? buildHoleCameraPath({ tee: hole.tees[0], green: hole.green }) : null),
    [hole]
  );

  // The clock only ever moves relative to when play (re)started, so pausing holds the
  // frame and resuming continues from it rather than snapping back to the tee.
  const playedUpToRef = useRef(0);
  const rafRef = useRef<number | null>(null);

  useEffect(() => {
    playedUpToRef.current = 0;
    setClockMs(0);
  }, [holeIndex, holes]);

  const advance = useCallback(() => {
    setHoleIndex((index) => {
      if (index + 1 < holes.length) return index + 1;
      // The last green is the end of the show; hold there rather than looping.
      setPlaying(false);
      return index;
    });
  }, [holes.length]);

  useEffect(() => {
    // A stored video drives its own clock; the animation loop only runs the 2D flight.
    if (!playing || !path || videoUrl) return undefined;

    const startedAt = Date.now() - playedUpToRef.current;
    const tick = () => {
      const elapsed = Date.now() - startedAt;
      playedUpToRef.current = elapsed;
      if (elapsed >= path.totalMs) {
        playedUpToRef.current = 0;
        advance();
        return;
      }
      setClockMs(elapsed);
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);

    return () => {
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
    };
  }, [advance, path, playing, videoUrl]);

  const camera = useMemo(() => (path ? path.cameraAt(clockMs) : null), [clockMs, path]);

  const polygons = useMemo<MapOverlayPolygon[]>(
    () =>
      (hole?.shapes ?? []).map((shape) => ({
        id: shape.id,
        ring: shape.ring,
        color: OVERLAY_COLORS[shape.kind] ?? '#FFFFFF',
      })),
    [hole]
  );

  // Warms the next hole's tiles while this hole plays, so the cut is not a blank mosaic.
  // Web leaves it to the browser's own cache; Image.prefetch is a native affair.
  useEffect(() => {
    if (Platform.OS === 'web') return;
    const next = holes[holeIndex + 1];
    if (!next?.green) return;
    const nextPath = buildHoleCameraPath({ tee: next.tees[0], green: next.green });
    const template = getSatelliteTileUrlTemplate();
    for (const at of [0, nextPath.durationMs / 2, nextPath.durationMs]) {
      const layout = computeLayoutAt({
        center: nextPath.cameraAt(at).center,
        spanMeters: nextPath.cameraAt(at).spanMeters,
        bearingDegrees: nextPath.cameraAt(at).bearingDegrees,
        ...PREFETCH_VIEWPORT,
      });
      for (const tile of layout?.tiles ?? []) {
        // Best-effort warm-up; the RN test renderer's Image stub returns no promise at all.
        void Image.prefetch(buildTileUrl(template, tile))?.catch?.(() => {});
      }
    }
  }, [holeIndex, holes]);

  const stepTo = useCallback(
    (index: number) => {
      if (index < 0 || index >= holes.length) return;
      setHoleIndex(index);
      setPlaying(true);
    },
    [holes.length]
  );

  const caption = useMemo(() => {
    if (!hole) return '';
    const pieces = [t('tourPreview.holeCaption', { number: hole.holeNumber })];
    if (hole.par !== null) pieces.push(t('tourPreview.parShort', { par: hole.par }));
    if (hole.strokeIndex !== null) {
      pieces.push(t('tourPreview.strokeIndexShort', { strokeIndex: hole.strokeIndex }));
    }
    if (hole.lengthMeters !== null && hole.lengthMeters > 0) {
      pieces.push(formatHoleDistance(hole.lengthMeters, distanceUnit));
    }
    return pieces.join(' · ');
  }, [distanceUnit, hole, t]);

  return (
    <View style={styles.container} testID="flyover-player">
      <View style={styles.header}>
        <Text style={styles.title} numberOfLines={1}>
          {courseName}
        </Text>
        <Pressable
          accessibilityRole="button"
          testID="flyover-close"
          onPress={onClose}
          style={styles.closeButton}
        >
          <Text style={styles.closeText}>✕</Text>
        </Pressable>
      </View>

      {loading ? (
        <View style={styles.stateBox} testID="flyover-loading">
          <ActivityIndicator color={appColors.accent.primary} />
        </View>
      ) : unavailable || !hole || !camera ? (
        <View style={styles.stateBox} testID="flyover-unavailable">
          <Text style={styles.stateText}>{t('tourPreview.courseNotMapped')}</Text>
        </View>
      ) : (
        <>
          {videoUrl ? (
            <Video
              testID="flyover-video"
              source={{ uri: videoUrl }}
              style={styles.video}
              resizeMode={ResizeMode.CONTAIN}
              shouldPlay={playing}
              onPlaybackStatusUpdate={(status: AVPlaybackStatus) => {
                if (status.isLoaded && status.didJustFinish) advance();
              }}
            />
          ) : (
            <HoleMapCanvas
              tees={hole.tees}
              green={hole.green}
              loading={false}
              unavailable={false}
              polygons={polygons}
              camera={camera}
            />
          )}
          <Text style={styles.caption} testID="flyover-caption">
            {caption}
          </Text>
          <View style={styles.controls}>
            <Pressable
              accessibilityRole="button"
              testID="flyover-previous"
              disabled={holeIndex === 0}
              onPress={() => stepTo(holeIndex - 1)}
              style={[styles.controlButton, holeIndex === 0 ? styles.controlDisabled : null]}
            >
              <Text style={styles.controlText}>{t('tourPreview.previousHole')}</Text>
            </Pressable>
            <Pressable
              accessibilityRole="button"
              testID="flyover-play-pause"
              onPress={() => setPlaying((value) => !value)}
              style={styles.controlButton}
            >
              <Text style={styles.controlText}>
                {playing ? t('tourPreview.pause') : t('tourPreview.play')}
              </Text>
            </Pressable>
            <Pressable
              accessibilityRole="button"
              testID="flyover-next"
              disabled={holeIndex >= holes.length - 1}
              onPress={() => stepTo(holeIndex + 1)}
              style={[
                styles.controlButton,
                holeIndex >= holes.length - 1 ? styles.controlDisabled : null,
              ]}
            >
              <Text style={styles.controlText}>{t('tourPreview.nextHole')}</Text>
            </Pressable>
          </View>
        </>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    backgroundColor: '#07200D',
    borderRadius: 12,
    padding: 12,
    gap: 10,
  },
  header: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' },
  title: { color: '#FFFFFF', fontSize: 16, fontWeight: '700', flexShrink: 1 },
  closeButton: { padding: 6 },
  closeText: { color: '#FFFFFF', fontSize: 16 },
  stateBox: {
    height: 220,
    alignItems: 'center',
    justifyContent: 'center',
  },
  stateText: { color: appColors.text.primary, fontSize: 14, textAlign: 'center' },
  video: { width: '100%', aspectRatio: 16 / 9, borderRadius: 10, backgroundColor: '#000000' },
  caption: {
    color: '#FFFFFF',
    fontSize: 14,
    fontWeight: '600',
    textAlign: 'center',
    fontVariant: ['tabular-nums'],
  },
  controls: { flexDirection: 'row', justifyContent: 'center', gap: 10 },
  controlButton: {
    paddingVertical: 8,
    paddingHorizontal: 14,
    borderRadius: 8,
    backgroundColor: 'rgba(255,255,255,0.12)',
  },
  controlDisabled: { opacity: 0.4 },
  controlText: { color: '#FFFFFF', fontSize: 13, fontWeight: '600' },
});
